import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from "react"
import { getAnalyticsService } from "../services/analytics"
import { useAppContext } from "../hooks/context"
import eventBus from "../hooks/eventBus"
import { createMapAnalytics } from "./mapAnalytics"

/**
 * React context for managing analytics and feature flags via PostHog.
 * Provides access to feature flag evaluation and analytics tracking.
 */
export const AnalyticsContext = createContext(null)

/**
 * @typedef {Object} AnalyticsContextResult
 * @property {boolean} isOptedIn - Boolean indicating if the user has opted in to analytics tracking
 * @property {(options: {flagKey: string, defaultValue: boolean}) => boolean} isFeatureEnabled
 * @property {(flagKey: string) => Object} getFeaturePayload
 * @property {() => void} reset
 * @property {(pageName: string, properties?: Object) => void} pageView
 * @property {(eventName: string, properties?: Object) => void} capture
 * @property {Object} map - Map analytics tracking methods
 * @property {Function} map.trackViewChanged - Track view type changes
 * @property {Function} map.trackDatasetChanged - Track dataset changes
 * @property {Function} map.trackMetricChanged - Track metric changes
 * @property {Function} map.trackFilterApplied - Track filter applications
 * @property {Function} map.trackSearchPerformed - Track search operations
 * @property {Function} map.trackZoneSelected - Track zone selections
 * @property {Function} map.trackDaysChanged - Track day filter changes
 * @property {Function} map.trackHourChanged - Track hour changes
 * @property {Function} map.trackPlaybackStarted - Track playback start
 * @property {Function} map.trackPlaybackSpeedChanged - Track playback speed changes
 * @property {Function} map.trackGraphOpened - Track graph opens
 * @property {Function} map.trackGraphTypeChanged - Track graph type changes
 * @property {Function} map.trackCampusFlowSessionStart - Track Campus Flow session start
 * @property {Function} map.trackCampusFlowInteraction - Track Campus Flow interaction
 */

/**
 * Hook to access the AnalyticsContext.
 *
 * @returns {AnalyticsContextResult} The analytics context
 * @throws {Error} If used outside of an AnalyticsProvider
 */
export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext)
  if (!context) {
    throw new Error("useAnalyticsContext must be used within an AnalyticsProvider")
  }
  return context
}

/**
 * Analytics context provider component.
 * Subscribes to AnalyticsService for reactive updates.
 */
export const AnalyticsProvider = ({ children }) => {
  const { school, user } = useAppContext()
  const analyticsService = getAnalyticsService()

  const { isSetup, isOptedIn } = useSyncExternalStore(
    analyticsService.subscribe,
    analyticsService.getSnapshot
  )

  const previousUserIdRef = useRef(null)
  const previousSchoolIdRef = useRef(null)
  const previousIsOptedInRef = useRef(isOptedIn)

  // Set user/school when they change OR when user opts back in
  useEffect(() => {
    const currentUserId = user?.id || null
    const currentSchoolId = school?.id || null

    // Detect opt-in transition (was opted out, now opted in)
    const justOptedIn = isOptedIn && !previousIsOptedInRef.current

    if (currentUserId && (currentUserId !== previousUserIdRef.current || justOptedIn)) {
      if (previousUserIdRef.current && !justOptedIn) {
        analyticsService.reset()
      }
      analyticsService.setUser(currentUserId, {
        email: user.email,
        name: user.name,
      })
    }

    if (currentSchoolId && (currentSchoolId !== previousSchoolIdRef.current || justOptedIn)) {
      analyticsService.setUserProperties({
        tenant_id: school.id,
        tenant_name: school.name,
      })
    }

    previousUserIdRef.current = currentUserId
    previousSchoolIdRef.current = currentSchoolId
    previousIsOptedInRef.current = isOptedIn
  }, [analyticsService, school, user, isOptedIn])

  // Listen for logout events to clear analytics
  useEffect(() => {
    const handleLogout = () => {
      analyticsService.clear()
    }

    eventBus.on("logout", handleLogout)
    return () => eventBus.off("logout", handleLogout)
  }, [analyticsService])

  // Callbacks regenerate when service state changes
  const isFeatureEnabled = useCallback(
    ({ flagKey, defaultValue }) => {
      if (!isSetup) return defaultValue
      return analyticsService.isFeatureEnabled(flagKey, defaultValue)
    },
    [analyticsService, isSetup]
  )

  const getFeaturePayload = useCallback(
    (flagKey) => {
      if (!isSetup) return {}
      return analyticsService.getFeaturePayload(flagKey)
    },
    [analyticsService, isSetup]
  )

  const pageView = useCallback(
    (pageName, properties) => {
      if (!isOptedIn) return
      analyticsService.pageView(pageName, properties)
    },
    [analyticsService, isOptedIn]
  )

  const capture = useCallback(
    (eventName, properties) => {
      if (!isOptedIn) return
      analyticsService.capture(eventName, properties)
    },
    [analyticsService, isOptedIn]
  )

  const reset = useCallback(
    () => analyticsService.reset(),
    [analyticsService]
  )

  // Create domain analytics
  const map = useMemo(
    () => createMapAnalytics({ capture, eventBus }),
    [capture]
  )

  // Register domain listeners
  useEffect(() => map.registerListeners(), [map])

  const value = useMemo(
    () => ({ isOptedIn, isFeatureEnabled, getFeaturePayload, pageView, capture, reset, map }),
    [isOptedIn, isFeatureEnabled, getFeaturePayload, pageView, capture, reset, map]
  )

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>
}
